'use client'

import { VisuallyHidden } from '@mantine/core'
import type { SearchTermSuggestion } from './universalSearchTypes'
import { SearchTermSuggestionRow } from './SearchTermSuggestionRow'
import { SearchSubmitRow } from './SearchSubmitRow'
import { TYPEAHEAD_MAX_ROWS } from './universalSearchTypes'
import classes from './UniversalSearch.module.css'

interface SearchSuggestionPanelProps {
    /** Current search text. */
    query: string
    /** Starter chips shown when the search field is empty. */
    termSuggestions: SearchTermSuggestion[]
    /** Live text matches shown while the user is typing. */
    searchSuggestions: SearchTermSuggestion[]
    /** True while waiting for debounced search results or the API request is in flight. */
    isTypeaheadPending: boolean
    /** Called when the user picks a starter suggestion chip. */
    onTermSelect: (suggestion: SearchTermSuggestion) => void
    /** Called when the user submits a full search for the current query. */
    onSubmit: () => void
    /** Use mobile for the mobile panel layout; default for the desktop dropdown. */
    variant?: 'default' | 'mobile'
}

/**
 * The suggestion list inside search. Shows starter rows when empty, live text
 * matches while typing, and a search submit row (top on mobile, bottom on desktop).
 */
export function SearchSuggestionPanel({
    query,
    termSuggestions,
    searchSuggestions,
    isTypeaheadPending,
    onTermSelect,
    onSubmit,
    variant = 'default',
}: SearchSuggestionPanelProps) {
    const trimmedQuery = query.trim()
    const showTermSuggestions = trimmedQuery.length === 0
    const showTypeaheadResults = trimmedQuery.length > 0
    const isMobileVariant = variant === 'mobile'
    const visibleTermSuggestions = termSuggestions.slice(0, TYPEAHEAD_MAX_ROWS)
    const visibleSearchSuggestions = searchSuggestions.slice(0, TYPEAHEAD_MAX_ROWS)
    const suggestionsStatus =
        showTypeaheadResults && !isTypeaheadPending
            ? getSuggestionsStatus(visibleSearchSuggestions.length, trimmedQuery)
            : ''

    return (
        <div
            className={[classes.suggestionPanel, isMobileVariant ? classes.suggestionPanelMobile : '']
                .filter(Boolean)
                .join(' ')}
        >
            {showTermSuggestions && (
                <div className={classes.suggestionRowList}>
                    {visibleTermSuggestions.map((suggestion) => (
                        <SearchTermSuggestionRow
                            key={`${suggestion.label}-${suggestion.query}`}
                            suggestion={suggestion}
                            onSelect={onTermSelect}
                            variant={variant}
                        />
                    ))}
                </div>
            )}

            {showTypeaheadResults && (
                <div
                    className={classes.suggestionRowList}
                    {...(isTypeaheadPending && searchSuggestions.length === 0
                        ? { role: 'status', 'aria-label': 'Loading search results' }
                        : {})}
                >
                    {isMobileVariant && <SearchSubmitRow query={query} onSubmit={onSubmit} variant={variant} />}

                    {visibleSearchSuggestions.map((suggestion) => (
                        <SearchTermSuggestionRow
                            key={`${suggestion.label}-${suggestion.query}`}
                            suggestion={suggestion}
                            onSelect={onTermSelect}
                            variant={variant}
                        />
                    ))}

                    {!isTypeaheadPending && searchSuggestions.length === 0 && (
                        <div className={classes.emptyState}>No matching suggestions</div>
                    )}

                    {!isMobileVariant && <SearchSubmitRow query={query} onSubmit={onSubmit} variant={variant} />}
                </div>
            )}

            <VisuallyHidden role="status" aria-live="polite">
                {suggestionsStatus}
            </VisuallyHidden>
        </div>
    )
}

function getSuggestionsStatus(count: number, query: string) {
    if (count === 0) return `No suggestions for “${query}”.`
    return count === 1 ? '1 suggestion.' : `${count} suggestions.`
}
